import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Camera, KeyRound, Layers, ListChecks, MessageCircle, Trash2, UserRound } from "lucide-react";
import {
  User, RuleType, BranchAccessRequestDetail,
  createUser, updateUser, deleteUser, sendInvite,
  updateUserBranchPermissions, updateUserPartnerPermissions, updateUserDepartmentMappings,
  grantBranchAccessRequest, denyBranchAccessRequest,
  listUserSubscriptions, adminUpsertSubscription, listSubscriptions, upsertSubscription,
  uploadUserPicture, deleteUserPicture, uploadProfilePicture, deleteProfilePicture,
  updateProfilePhone, requestBranchAccess, ReviewSettings,
  getOwnReviewSettings, updateOwnReviewSettings, getUserReviewSettings, updateUserReviewSettings,
} from "../../api/api";
import { useBranches, useKams, useMyBranchRequests, usePartners, usePendingBranchAccessRequests, useProfile, useUserDepartmentMappings } from "../../hooks/queries";
import { useAuth } from "../../hooks/useAuth";
import { ROLE_LABELS, actsAsKam } from "../../lib/roles";
import { toast } from "../../lib/toast";
import { buildVestigingOptions } from "../AlertRuleCard";
import { useChatWidget } from "../ChatWidget";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { UserAvatar } from "../ui/user-avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CHANGED_OUTLINE, CHANGED_RING, CheckTile, CheckTileGrid, FieldError, FieldRow, FormSection, sameSet } from "./fields";
import { AlertDrafts, AlertDraft, UserAlertsTab, alertDraftFromSub, alertInput, alertRuleTypesFor, changedAlertFields } from "./UserAlertsTab";
import { UserReviewTab, reviewChangeCount, reviewSettingsValid } from "./UserReviewTab";
import { DepartmentDraft, UserDepartmentsTab, departmentDraftFromMappings, departmentMappingsFromDraft, departmentsChanged } from "./UserDepartmentsTab";
import { useHelp } from "../help/HelpProvider";
import { TOURS } from "../../lib/tours";
import { emitTourEvent } from "../../lib/tourEvents";
import { cn } from "@/lib/utils";

type Role = User["role"];
export type UserDialogTab = "account" | "toegang" | "meldingen" | "afdelingen" | "review";
type Tab = UserDialogTab;

interface AccountDraft {
  name: string;
  email: string;
  phone: string;
  role: Role;
  partner_id: number | null;
  kam_id: number | null;
  secondary_kam_id: number | null;
  can_act_as_kam: boolean;
  send_invite: boolean;
}

// The dialog stages every edit and saves them together. `departments` and
// `alerts` load asynchronously and stay null until they have.
interface Draft {
  account: AccountDraft;
  branchIds: number[];
  partnerIds: number[];
  requests: Record<number, "grant" | "deny">;
  departments: DepartmentDraft | null;
  alerts: AlertDrafts | null;
  review: ReviewSettings | null;
  // undefined keeps the saved picture, null removes it, a File replaces it.
  picture?: File | null;
}

// Radix Select items can't use "" as a value, so "no secondary KAM" needs a sentinel.
const NO_SECONDARY_KAM = "none";
const PARTNER_ROLES: Role[] = ["hmp", "partner_admin"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors the upload checks in backend/internal/service/profile.go.
const PICTURE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PICTURE_BYTES = 5 * 1024 * 1024;

function initialDraft(user: User | null, viewerPartnerId: number | null): Draft {
  return {
    account: user
      ? {
          name: user.name, email: user.email, phone: user.phone ?? "", role: user.role,
          partner_id: user.partner_id, kam_id: user.kam_id, secondary_kam_id: user.secondary_kam_id,
          can_act_as_kam: user.can_act_as_kam, send_invite: true,
        }
      : {
          name: "", email: "", phone: "", role: "hmp", partner_id: viewerPartnerId, kam_id: null,
          secondary_kam_id: null, can_act_as_kam: false, send_invite: true,
        },
    branchIds: user?.branch_ids ?? [],
    partnerIds: user?.partner_ids ?? [],
    requests: {},
    departments: null,
    alerts: null,
    review: null,
  };
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

function requestTime(iso: string) {
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

interface Props {
  // null opens the dialog for a new account.
  user: User | null;
  initialTab?: Tab;
  onClose: () => void;
}

export function UserDialog({ user, initialTab = "account", onClose }: Props) {
  const qc = useQueryClient();
  const { user: viewer } = useAuth();
  const isAdmin = viewer?.role === "admin";
  const isPartnerAdmin = viewer?.role === "partner_admin";
  const isNew = user === null;
  const isSelf = !!user && viewer?.id === user.id;
  const help = useHelp();
  const toursDone = TOURS.filter(t => help.completed.includes(t.id)).length;

  const [orig, setOrig] = useState<Draft>(() => initialDraft(user, isPartnerAdmin ? viewer?.partner_id ?? null : null));
  const [cur, setCur] = useState<Draft>(orig);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [confirm, setConfirm] = useState<null | "close" | "delete">(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showErrors, setShowErrors] = useState(!isNew);

  const acc = cur.account;
  const oacc = orig.account;
  const setAccount = (patch: Partial<AccountDraft>) => setCur(c => ({ ...c, account: { ...c.account, ...patch } }));

  // ── What this viewer may change ─────────────────────────────
  // Mirrors the route gates: PUT /users/:id and invites/deletes are admin-only,
  // partner_admin may create accounts within their own partner, and branch
  // permissions/requests are open to admin, kam and partner_admin.
  const canEditAccount = isAdmin || (isNew && isPartnerAdmin);
  const needsPartner = PARTNER_ROLES.includes(acc.role) && !isPartnerAdmin;
  const needsKam = acc.role === "hmp" && !isPartnerAdmin;
  const showBranches = acc.role === "hmp";
  const showPartners = actsAsKam(acc);
  const canEditPartners = isAdmin;
  const partnerChanged = acc.partner_id !== oacc.partner_id;
  const canEditDepartments = !isNew && (isAdmin || viewer?.role === "kam")
    && !!user?.partner_id && PARTNER_ROLES.includes(user.role) && !partnerChanged;
  // Everyone manages their own phone, picture and alerts through their own
  // endpoints; admins can do the same for anyone else.
  const canEditPhone = canEditAccount || isSelf;
  const canEditPicture = !isNew && (isAdmin || isSelf);
  const canEditAlerts = !isNew && (isAdmin || isSelf);
  // Only hiring managers have vacature-review settings (route-gated the same way).
  const canEditReview = !isNew && user.role === "hmp" && acc.role === "hmp" && (isAdmin || isSelf);
  const canReviewRequests = !isNew && !!viewer && ["admin", "kam", "partner_admin"].includes(viewer.role);
  // A hiring manager can't grant themselves vestigingen, only ask for them.
  const requestsOwnBranches = isSelf && user?.role === "hmp";

  // ── Data ────────────────────────────────────────────────────
  const { data: partners = [] } = usePartners();
  const { data: kams = [] } = useKams(needsKam || isAdmin);
  const { data: branches = [] } = useBranches(acc.partner_id ?? undefined, showBranches && !!acc.partner_id);
  const { data: pending = [] } = usePendingBranchAccessRequests(canReviewRequests);
  const requests = useMemo(
    () => (user ? pending.filter((r: BranchAccessRequestDetail) => r.user_id === user.id && r.status === "pending") : []),
    [pending, user],
  );
  const { data: myRequests = [], refetch: refetchMyRequests } = useMyBranchRequests(requestsOwnBranches);
  const [requesting, setRequesting] = useState<number | null>(null);
  const { data: profile } = useProfile(isSelf);
  const { openAlgemeenChat } = useChatWidget();
  const { data: mappings } = useUserDepartmentMappings(canEditDepartments ? user?.id : undefined);
  const { data: subs } = useQuery({
    queryKey: isSelf ? ["subscriptions"] : ["user-subscriptions", user?.id],
    queryFn: () => (isSelf ? listSubscriptions() : listUserSubscriptions(user!.id)),
    enabled: canEditAlerts,
  });
  const { data: reviewSettings } = useQuery({
    queryKey: isSelf ? ["review-settings"] : ["review-settings", user?.id],
    queryFn: () => (isSelf ? getOwnReviewSettings() : getUserReviewSettings(user!.id)),
    enabled: canEditReview,
  });

  // ── Profile picture ─────────────────────────────────────────
  const pictureInputRef = useRef<HTMLInputElement>(null);
  const [savedPictureUrl, setSavedPictureUrl] = useState(user?.profile_picture_url ?? null);
  const [pictureError, setPictureError] = useState("");
  const previewUrl = useMemo(() => (cur.picture ? URL.createObjectURL(cur.picture) : null), [cur.picture]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const pictureUrl = cur.picture === undefined ? savedPictureUrl : previewUrl;
  const pictureChanged = cur.picture !== undefined;

  function handlePictureSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!PICTURE_TYPES.includes(file.type)) { setPictureError("Kies een JPG-, PNG- of WebP-afbeelding."); return; }
    if (file.size > MAX_PICTURE_BYTES) { setPictureError("De afbeelding mag maximaal 5 MB zijn."); return; }
    setPictureError("");
    setCur(c => ({ ...c, picture: file }));
  }

  // Requesting a vestiging is sent right away: it isn't a setting of this
  // account but a question to the people who manage it.
  async function handleRequestBranch(branchId: number) {
    setRequesting(branchId);
    try {
      await requestBranchAccess(branchId);
      await refetchMyRequests();
      toast("Toegang aangevraagd", { kind: "success" });
    } catch { /* handled by the global error snackbar */ }
    finally { setRequesting(null); }
  }

  // Alerts are about the saved user, not the staged role.
  const isWavesUser = !!user && (user.role === "kam" || user.role === "admin");
  const ruleTypes = useMemo(() => (user ? alertRuleTypesFor(user) : []), [user]);
  const alertPartnerId = user && PARTNER_ROLES.includes(user.role) ? user.partner_id : null;
  const { data: alertBranches = [] } = useBranches(alertPartnerId ?? undefined, canEditAlerts);
  const vestigingOptions = useMemo(() => buildVestigingOptions(alertBranches, partners), [alertBranches, partners]);
  const { data: departmentBranches = [] } = useBranches(user?.partner_id ?? undefined, canEditDepartments);

  useEffect(() => {
    if (!mappings) return;
    const d = departmentDraftFromMappings(mappings);
    setOrig(o => (o.departments ? o : { ...o, departments: d }));
    setCur(c => (c.departments ? c : { ...c, departments: d }));
  }, [mappings]);

  useEffect(() => {
    if (!subs) return;
    const a: AlertDrafts = Object.fromEntries(ruleTypes.map(rt => [rt, alertDraftFromSub(rt, subs.find(s => s.rule_type === rt))]));
    setOrig(o => (o.alerts ? o : { ...o, alerts: a }));
    setCur(c => (c.alerts ? c : { ...c, alerts: a }));
  }, [subs, ruleTypes]);

  useEffect(() => {
    if (!reviewSettings) return;
    setOrig(o => (o.review ? o : { ...o, review: reviewSettings }));
    setCur(c => (c.review ? c : { ...c, review: reviewSettings }));
  }, [reviewSettings]);

  // ── Changes ─────────────────────────────────────────────────
  const accCh = {
    name: acc.name.trim() !== oacc.name,
    email: acc.email.trim() !== oacc.email,
    phone: acc.phone.trim() !== oacc.phone,
    role: acc.role !== oacc.role,
    can_act_as_kam: acc.role === "admin" && acc.can_act_as_kam !== oacc.can_act_as_kam,
    partner_id: needsPartner && partnerChanged,
    kam_id: needsKam && acc.kam_id !== oacc.kam_id,
    secondary_kam_id: needsKam && acc.secondary_kam_id !== oacc.secondary_kam_id,
  };
  const accountChanged = Object.values(accCh).some(Boolean);
  const branchesChanged = showBranches && !sameSet(cur.branchIds, orig.branchIds);
  const partnersChanged = showPartners && canEditPartners && !sameSet(cur.partnerIds, orig.partnerIds);
  const decisions = Object.entries(cur.requests);
  const deptChanged = canEditDepartments && !!cur.departments && !!orig.departments && departmentsChanged(cur.departments, orig.departments);
  const changedRules = canEditAlerts && cur.alerts && orig.alerts
    ? ruleTypes.filter(rt => cur.alerts![rt] && orig.alerts![rt] && changedAlertFields(cur.alerts![rt]!, orig.alerts![rt]!).size > 0)
    : [];
  const alertChangeCount = changedRules.reduce((n, rt) => n + changedAlertFields(cur.alerts![rt]!, orig.alerts![rt]!).size, 0);

  const reviewChanges = canEditReview && cur.review && orig.review ? reviewChangeCount(cur.review, orig.review) : 0;

  const changeCount = Object.values(accCh).filter(Boolean).length + (pictureChanged ? 1 : 0)
    + (branchesChanged ? 1 : 0) + (partnersChanged ? 1 : 0) + decisions.length + (deptChanged ? 1 : 0) + alertChangeCount
    + reviewChanges;

  // ── Validation ──────────────────────────────────────────────
  // Existing accounts are only checked on what changes, so an account that is
  // already incomplete (e.g. an hmp without KAM, an open Taken task) can still
  // get its other settings saved.
  const strict = isNew || accCh.role;
  const errors = {
    name: canEditAccount && !acc.name.trim() ? "Vul een naam in." : "",
    email: canEditAccount && (isNew || accCh.email) && !EMAIL_RE.test(acc.email.trim()) ? "Vul een geldig e-mailadres in." : "",
    phone: canEditPhone && (isNew || accCh.phone) && acc.phone.trim() && acc.phone.replace(/[^0-9]/g, "").length < 9 ? "Vul een geldig telefoonnummer in." : "",
    partner: canEditAccount && strict && needsPartner && !acc.partner_id ? "Kies een partner." : "",
    kam: canEditAccount && strict && needsKam && !acc.kam_id ? "Kies een account manager." : "",
  };
  const reviewValid = !reviewChanges || !cur.review || reviewSettingsValid(cur.review);
  const valid = !Object.values(errors).some(Boolean) && reviewValid;
  const err = (k: keyof typeof errors) => (showErrors ? errors[k] : "");

  // ── Actions ─────────────────────────────────────────────────
  function invalidate() {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["kams"] });
    qc.invalidateQueries({ queryKey: ["branch-requests"] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
    if (user) {
      qc.invalidateQueries({ queryKey: ["user-subscriptions", user.id] });
      qc.invalidateQueries({ queryKey: ["department-mappings", user.id] });
      qc.invalidateQueries({ queryKey: ["review-settings"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "hmp"] });
    }
    if (isSelf) {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
    }
  }

  // Runs once the dialog has actually closed, e.g. opening the chat after
  // the "Wijzigingen opslaan?" question is answered.
  const afterClose = useRef<(() => void) | null>(null);
  function close() {
    onClose();
    afterClose.current?.();
  }

  function cancelConfirm() {
    afterClose.current = null;
    setConfirm(null);
  }

  function requestClose(then?: () => void) {
    if (saving || busy) return;
    if (confirm) { cancelConfirm(); return; }
    afterClose.current = then ?? null;
    if (changeCount > 0 || (isNew && acc.name.trim())) setConfirm("close");
    else close();
  }

  async function save() {
    if (saving) return;
    if (!valid) { setShowErrors(true); setTab(reviewValid ? "account" : "review"); cancelConfirm(); return; }
    if (!isNew && changeCount === 0) return;
    setSaving(true);
    setSaveError("");
    setConfirm(null);
    try {
      if (isNew) await saveNew();
      else await saveExisting();
      close();
    } catch (e) {
      afterClose.current = null;
      setSaveError(e instanceof Error ? e.message : "Fout bij opslaan");
    } finally {
      setSaving(false);
      invalidate();
    }
  }

  async function saveNew() {
    const created = await createUser({
      name: acc.name.trim(), email: acc.email.trim(), role: acc.role,
      partner_id: PARTNER_ROLES.includes(acc.role) ? acc.partner_id ?? undefined : undefined,
      kam_id: needsKam ? acc.kam_id ?? undefined : undefined,
      secondary_kam_id: needsKam ? acc.secondary_kam_id : undefined,
      can_act_as_kam: acc.role === "admin" && acc.can_act_as_kam,
      send_invite: acc.send_invite,
    }, { silenceErrors: true });
    // The create endpoint has no phone field; an admin sets it right after.
    if (isAdmin && acc.phone.trim()) await updateUser(created.id, { phone: acc.phone.trim() }, { silenceErrors: true });
    if (showBranches && cur.branchIds.length) await updateUserBranchPermissions(created.id, cur.branchIds, { silenceErrors: true });
    if (showPartners && isAdmin && cur.partnerIds.length) await updateUserPartnerPermissions(created.id, cur.partnerIds, { silenceErrors: true });
    toast(acc.send_invite
      ? `Account aangemaakt · uitnodiging verstuurd naar ${acc.email.trim()}`
      : `Account voor ${acc.name.trim()} aangemaakt`, { kind: "success" });
  }

  // Saves step by step, marking each step as saved as soon as it succeeds, so
  // a failure halfway leaves only the unsaved part counted as a change.
  async function saveExisting() {
    const id = user!.id;
    const n = changeCount;
    if (accountChanged && canEditAccount) {
      await updateUser(id, {
        name: acc.name.trim(), email: acc.email.trim(), phone: acc.phone.trim(), role: acc.role,
        partner_id: PARTNER_ROLES.includes(acc.role) ? acc.partner_id ?? undefined : undefined, kam_id: acc.kam_id ?? undefined,
        secondary_kam_id: needsKam ? acc.secondary_kam_id : undefined,
        can_act_as_kam: acc.role === "admin" && acc.can_act_as_kam,
      }, { silenceErrors: true });
      const saved = { ...acc, name: acc.name.trim(), email: acc.email.trim(), phone: acc.phone.trim() };
      setOrig(o => ({ ...o, account: saved }));
    } else if (accCh.phone && isSelf) {
      // The only account field anyone may change on their own account.
      await updateProfilePhone(acc.phone.trim(), { silenceErrors: true });
      setOrig(o => ({ ...o, account: { ...o.account, phone: acc.phone.trim() } }));
    }
    if (pictureChanged) {
      const file = cur.picture;
      const opts = { silenceErrors: true };
      const saved = isSelf
        ? await (file ? uploadProfilePicture(file, opts) : deleteProfilePicture(opts))
        : await (file ? uploadUserPicture(id, file, opts) : deleteUserPicture(id, opts));
      setSavedPictureUrl(saved.profile_picture_url);
      setCur(c => ({ ...c, picture: undefined }));
    }
    for (const [reqId, decision] of decisions) {
      if (decision === "grant") await grantBranchAccessRequest(Number(reqId), { silenceErrors: true });
      else await denyBranchAccessRequest(Number(reqId), { silenceErrors: true });
      setCur(c => { const { [Number(reqId)]: _, ...rest } = c.requests; return { ...c, requests: rest }; });
    }
    if (branchesChanged) {
      await updateUserBranchPermissions(id, cur.branchIds, { silenceErrors: true });
      setOrig(o => ({ ...o, branchIds: cur.branchIds }));
    }
    if (partnersChanged) {
      await updateUserPartnerPermissions(id, cur.partnerIds, { silenceErrors: true });
      setOrig(o => ({ ...o, partnerIds: cur.partnerIds }));
    }
    if (deptChanged && cur.departments) {
      await updateUserDepartmentMappings(id, departmentMappingsFromDraft(cur.departments), { silenceErrors: true });
      setOrig(o => ({ ...o, departments: cur.departments }));
    }
    for (const rt of changedRules) {
      const d = cur.alerts![rt]!;
      const body = alertInput(rt, d, isWavesUser);
      await (isSelf ? upsertSubscription(rt, body) : adminUpsertSubscription(id, rt, body));
      setOrig(o => ({ ...o, alerts: { ...o.alerts, [rt]: d } }));
    }
    if (reviewChanges && cur.review) {
      const opts = { silenceErrors: true };
      const saved = isSelf ? await updateOwnReviewSettings(cur.review, opts) : await updateUserReviewSettings(id, cur.review, opts);
      setOrig(o => ({ ...o, review: saved }));
      setCur(c => ({ ...c, review: saved }));
    }
    emitTourEvent("settings-saved");
    toast(isSelf
      ? `${plural(n, "wijziging", "wijzigingen")} opgeslagen`
      : `${plural(n, "wijziging", "wijzigingen")} opgeslagen voor ${acc.name.trim()}`, { kind: "success" });
  }

  async function handleInvite() {
    if (!user) return;
    setBusy(true);
    try {
      await sendInvite(user.id);
      toast(`Uitnodiging verstuurd naar ${user.email}`, { kind: "success" });
    } catch { /* handled by the global error snackbar */ }
    finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!user) return;
    setBusy(true);
    setSaveError("");
    try {
      await deleteUser(user.id, { silenceErrors: true });
      toast(`Account van ${user.name} verwijderd`, { kind: "success" });
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Fout bij verwijderen");
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  function decide(r: BranchAccessRequestDetail, decision: "grant" | "deny" | null) {
    setCur(c => {
      const requests = { ...c.requests };
      let branchIds = c.branchIds;
      if (decision) requests[r.id] = decision; else delete requests[r.id];
      if (decision === "grant" && !branchIds.includes(r.branch_id)) branchIds = [...branchIds, r.branch_id];
      // Undoing a grant takes the branch back out, unless they already had it.
      if (decision !== "grant" && c.requests[r.id] === "grant" && !orig.branchIds.includes(r.branch_id)) {
        branchIds = branchIds.filter(b => b !== r.branch_id);
      }
      return { ...c, requests, branchIds };
    });
  }

  const toggleIn = (list: number[], id: number) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  // ── Layout ──────────────────────────────────────────────────
  const openRequests = requests.filter(r => !cur.requests[r.id]).length;
  const menu: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: "account", label: "Account", icon: <UserRound /> },
    { id: "toegang", label: requestsOwnBranches ? "Vestigingen" : "Toegang", icon: <KeyRound />, badge: openRequests || undefined },
    ...(canEditAlerts ? [{ id: "meldingen" as Tab, label: "Meldingen", icon: <Bell /> }] : []),
    ...(canEditDepartments ? [{ id: "afdelingen" as Tab, label: "Afdelingen", icon: <Layers /> }] : []),
    ...(canEditReview ? [{ id: "review" as Tab, label: "Vacature-review", icon: <ListChecks /> }] : []),
  ];
  const activeTab = menu.some(m => m.id === tab) ? tab : "account";
  const tabLabel = menu.find(m => m.id === activeTab)!.label;
  const title = isNew ? acc.name.trim() || "Nieuw account" : user.name;
  const partnerName = (id: number | null) => partners.find(p => p.id === id)?.name ?? "";

  const footerStatus = saveError
    ? { text: saveError, cls: "text-status-alert-fg" }
    : showErrors && !valid
      ? { text: "Controleer de gemarkeerde velden", cls: "text-status-alert-fg" }
      : isNew
        ? { text: acc.send_invite ? "Ontvangt een uitnodiging per e-mail" : "Er wordt geen uitnodiging verstuurd", cls: "text-waves-muted" }
        : changeCount > 0
          ? { text: plural(changeCount, "wijziging", "wijzigingen"), cls: "text-waves-ink", dot: true }
          : { text: "Geen wijzigingen", cls: "text-waves-muted" };

  return (
    <Dialog open onOpenChange={o => { if (!o) requestClose(); }}>
      <DialogContent data-tour="settings" className="flex h-[min(680px,calc(100vh-32px))] max-w-[980px] flex-col gap-0 overflow-hidden p-0 sm:flex-row">
        {/* Side menu */}
        <div className="flex shrink-0 flex-col gap-3 border-b border-waves-line bg-waves-subtle px-3.5 py-4 sm:w-[240px] sm:gap-[18px] sm:overflow-y-auto sm:border-b-0 sm:border-r sm:py-6">
          <div className="flex items-center gap-2.5 px-1.5 pr-12 sm:border-b sm:border-waves-line sm:pb-4 sm:pr-1.5">
            {isNew
              ? <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-waves-lucht font-mono text-sm font-semibold">+</span>
              : <UserAvatar key={pictureUrl ?? ""} name={user.name} pictureUrl={pictureUrl} size={36} />}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[15px] font-bold">{title}</span>
              <span className="truncate text-xs text-waves-muted">
                {ROLE_LABELS[isNew ? acc.role : user.role] ?? acc.role}
                {!isNew && user.partner_id ? ` · ${partnerName(user.partner_id)}` : ""}
              </span>
            </div>
          </div>
          <nav data-tour="settings-nav" className="flex gap-0.5 overflow-x-auto sm:flex-col">
            {menu.map(m => {
              const on = m.id === activeTab;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-tour={`settings-tab-${m.id}`}
                  onClick={() => setTab(m.id)}
                  aria-current={on ? "page" : undefined}
                  className={cn(
                    "flex shrink-0 items-center gap-3 rounded-nav px-3 py-2.5 text-left text-[15px] transition-colors hover:bg-white [&_svg]:size-[18px] [&_svg]:shrink-0",
                    on ? "bg-white font-bold text-waves-ink shadow-[0_1px_2px_rgba(16,32,47,0.06)]" : "font-medium text-waves-body",
                  )}
                >
                  {m.icon}
                  <span className="flex-1">{m.label}</span>
                  {m.badge ? <span className="rounded-pill bg-status-alert-bg px-2 py-px text-xs font-bold text-status-alert-fg">{m.badge}</span> : null}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-4 border-b border-waves-line px-5 py-4 sm:py-[22px] sm:pl-8 sm:pr-20">
            <DialogTitle className="flex-1 text-[22px] tracking-[-0.02em]">{isNew ? `Nieuw account · ${tabLabel}` : tabLabel}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {isNew ? "Maak een nieuw account aan voor het partnerportaal." : isSelf ? "Je eigen accountinstellingen" : `Instellingen van ${user.name}`}
          </DialogDescription>

          <div
            data-tour={activeTab === "meldingen" ? "settings-alerts" : activeTab === "review" ? "settings-review" : undefined}
            className="flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-5 pb-8 pt-6 sm:px-8"
          >
            {activeTab === "account" && (
              <>
                {!canEditAccount && (
                  <div className="rounded-nav bg-waves-subtle px-4 py-3 text-sm text-waves-body">
                    {isSelf
                      ? "Je naam, rol en e-mail worden beheerd door Waves. Je telefoonnummer en foto kun je zelf aanpassen."
                      : "Alleen een beheerder kan de accountgegevens wijzigen."}
                  </div>
                )}
                <FormSection title="Persoonsgegevens">
                  {canEditPicture && (
                    <FieldRow label="Profielfoto" hint="JPG, PNG of WebP, max. 5 MB">
                      <div className="flex flex-wrap items-center gap-4">
                        <UserAvatar
                          key={pictureUrl ?? ""}
                          name={acc.name}
                          pictureUrl={pictureUrl}
                          size={64}
                          className={cn(pictureChanged && CHANGED_OUTLINE)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => pictureInputRef.current?.click()}>
                            <Camera /> {pictureUrl ? "Andere foto" : "Foto kiezen"}
                          </Button>
                          {pictureUrl && (
                            <Button variant="ghost" size="sm" onClick={() => setCur(c => ({ ...c, picture: savedPictureUrl ? null : undefined }))}>
                              <Trash2 /> Verwijderen
                            </Button>
                          )}
                          {pictureChanged && (
                            <button type="button" onClick={() => setCur(c => ({ ...c, picture: undefined }))} className="px-1 text-[13px] font-bold text-waves-accent hover:underline">
                              Herstel
                            </button>
                          )}
                        </div>
                        <input
                          ref={pictureInputRef}
                          type="file"
                          accept={PICTURE_TYPES.join(",")}
                          className="hidden"
                          onChange={handlePictureSelected}
                        />
                      </div>
                      {pictureError && <FieldError>{pictureError}</FieldError>}
                    </FieldRow>
                  )}
                  <FieldRow label="Naam">
                    <Input
                      value={acc.name}
                      disabled={!canEditAccount}
                      onChange={e => setAccount({ name: e.target.value })}
                      placeholder="Voor- en achternaam"
                      aria-label="Naam"
                      className={cn("rounded-pill px-[18px]", !isNew && accCh.name && CHANGED_RING)}
                    />
                    {err("name") && <FieldError>{err("name")}</FieldError>}
                  </FieldRow>
                  <FieldRow label="Contact" hint="E-mail en telefoon">
                    <Input
                      type="email"
                      value={acc.email}
                      disabled={!canEditAccount}
                      onChange={e => setAccount({ email: e.target.value })}
                      placeholder="naam@bedrijf.nl"
                      aria-label="E-mail"
                      className={cn("rounded-pill px-[18px]", !isNew && accCh.email && CHANGED_RING)}
                    />
                    {err("email") && <FieldError>{err("email")}</FieldError>}
                    {(isAdmin || !isNew) && (
                      <Input
                        type="tel"
                        value={acc.phone}
                        disabled={!canEditPhone}
                        onChange={e => setAccount({ phone: e.target.value })}
                        placeholder="06 12345678"
                        aria-label="Telefoonnummer"
                        className={cn("rounded-pill px-[18px]", !isNew && accCh.phone && CHANGED_RING)}
                      />
                    )}
                    {err("phone") && <FieldError>{err("phone")}</FieldError>}
                  </FieldRow>
                </FormSection>

                <FormSection title="Rol & partner" divided>
                  <FieldRow label="Rol" hint="Bepaalt wat de gebruiker ziet">
                    <Select
                      value={acc.role}
                      disabled={!canEditAccount || isSelf}
                      onValueChange={v => setAccount({ role: v as Role })}
                    >
                      <SelectTrigger aria-label="Rol" className={cn("max-w-[360px] rounded-pill px-[18px]", !isNew && accCh.role && CHANGED_RING)}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(isPartnerAdmin ? (["hmp", "partner_admin"] as Role[]) : (["hmp", "partner_admin", "kam", "admin"] as Role[])).map(r => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldRow>

                  {acc.role === "admin" && (
                    <FieldRow label="Key account manager" hint="Kan aan partners en hiring managers worden gekoppeld en krijgt KAM-meldingen">
                      <div className="flex items-center gap-3 sm:pt-2">
                        <Switch
                          checked={acc.can_act_as_kam}
                          disabled={!canEditAccount}
                          onCheckedChange={on => setAccount({ can_act_as_kam: on })}
                          aria-label="Kan als key account manager optreden"
                          className={cn(!isNew && accCh.can_act_as_kam && CHANGED_OUTLINE)}
                        />
                        <span className="text-sm text-waves-body">Kan als key account manager optreden</span>
                      </div>
                    </FieldRow>
                  )}

                  {needsPartner && (
                    <FieldRow label="Partner">
                      <Select
                        value={acc.partner_id ? String(acc.partner_id) : undefined}
                        disabled={!canEditAccount}
                        // Vestigingen belong to one partner, so switching partner clears them.
                        onValueChange={v => setCur(c => ({ ...c, account: { ...c.account, partner_id: Number(v) }, branchIds: [], requests: {} }))}
                      >
                        <SelectTrigger aria-label="Partner" className={cn("max-w-[360px] rounded-pill px-[18px]", !acc.partner_id && "text-waves-muted", !isNew && accCh.partner_id && CHANGED_RING)}>
                          <SelectValue placeholder="Kies een partner" />
                        </SelectTrigger>
                        <SelectContent>
                          {partners.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {err("partner") && <FieldError>{err("partner")}</FieldError>}
                    </FieldRow>
                  )}

                  {needsKam && (
                    <>
                      <FieldRow label="Account manager" hint="Vast aanspreekpunt bij Waves">
                        <Select
                          value={acc.kam_id ? String(acc.kam_id) : undefined}
                          disabled={!canEditAccount}
                          onValueChange={v => setAccount({ kam_id: Number(v), secondary_kam_id: acc.secondary_kam_id === Number(v) ? null : acc.secondary_kam_id })}
                        >
                          <SelectTrigger aria-label="Account manager" className={cn("max-w-[360px] rounded-pill px-[18px]", !acc.kam_id && "text-waves-muted", !isNew && accCh.kam_id && CHANGED_RING)}>
                            <SelectValue placeholder="Kies een account manager" />
                          </SelectTrigger>
                          <SelectContent>
                            {kams.map(k => <SelectItem key={k.id} value={String(k.id)}>{k.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {err("kam") && <FieldError>{err("kam")}</FieldError>}
                      </FieldRow>
                      <FieldRow label="Tweede account manager" hint="Vervanger of tweede aanspreekpunt">
                        <Select
                          value={acc.secondary_kam_id ? String(acc.secondary_kam_id) : NO_SECONDARY_KAM}
                          disabled={!canEditAccount || !acc.kam_id}
                          onValueChange={v => setAccount({ secondary_kam_id: v === NO_SECONDARY_KAM ? null : Number(v) })}
                        >
                          <SelectTrigger aria-label="Tweede account manager" className={cn("max-w-[360px] rounded-pill px-[18px]", !acc.secondary_kam_id && "text-waves-muted", !isNew && accCh.secondary_kam_id && CHANGED_RING)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_SECONDARY_KAM}>Geen</SelectItem>
                            {kams.filter(k => k.id !== acc.kam_id).map(k => <SelectItem key={k.id} value={String(k.id)}>{k.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                    </>
                  )}

                  {isNew && isPartnerAdmin && acc.role === "hmp" && (
                    <p className="rounded-nav bg-waves-subtle px-4 py-2.5 text-sm text-waves-body">
                      Er wordt automatisch een taak aangemaakt voor het toewijzen van een account manager.
                    </p>
                  )}
                </FormSection>

                {isSelf && (profile?.waves_contacts ?? []).length > 0 && (
                  <FormSection title="Contactpersoon bij Waves" divided>
                    {profile!.waves_contacts.map((contact, i) => (
                      <FieldRow key={contact.email} label={i === 0 ? "Contactpersoon" : "Tweede contactpersoon"}>
                        <div className="flex flex-wrap items-center gap-3 sm:pt-1">
                          <UserAvatar name={contact.name} size={40} />
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div className="truncate text-[15px] font-bold">{contact.name}</div>
                            <div className="truncate text-[13px] text-waves-muted">
                              {[contact.email, contact.phone].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                          {i === 0 && (
                            <Button variant="ink" size="sm" onClick={() => requestClose(() => openAlgemeenChat())}>
                              <MessageCircle /> Chat
                            </Button>
                          )}
                        </div>
                      </FieldRow>
                    ))}
                  </FormSection>
                )}

                {isSelf && help.enabled && (
                  <FormSection title="Hulp" divided>
                    <FieldRow label="Rondleidingen" hint={`Je hebt er ${toursDone} van de ${TOURS.length} afgerond`}>
                      <Button variant="outline" className="self-start" disabled={toursDone === 0} onClick={help.resetTours}>
                        Rondleidingen opnieuw
                      </Button>
                    </FieldRow>
                  </FormSection>
                )}

                {isNew && (
                  <FormSection title="Uitnodiging" divided>
                    <FieldRow label="Uitnodigingsmail" hint="Met een link om in te loggen">
                      <div className="flex items-center gap-3 sm:pt-2">
                        <Switch
                          checked={acc.send_invite}
                          onCheckedChange={on => setAccount({ send_invite: on })}
                          aria-label="Verstuur uitnodigingsmail"
                        />
                        <span className="text-sm text-waves-body">Verstuur direct na aanmaken</span>
                      </div>
                    </FieldRow>
                  </FormSection>
                )}

                {!isNew && isAdmin && !isSelf && (
                  <FormSection title="Accountstatus" divided>
                    <FieldRow label="Uitnodiging" hint="Stuurt de uitnodigingsmail opnieuw">
                      <Button variant="outline" className="self-start" disabled={busy} onClick={handleInvite}>
                        Uitnodiging opnieuw sturen
                      </Button>
                    </FieldRow>
                    <FieldRow label="Verwijderen" hint="Kan daarna niet meer inloggen">
                      <Button variant="outline" className="self-start text-status-alert-fg" disabled={busy} onClick={() => setConfirm("delete")}>
                        Account verwijderen
                      </Button>
                    </FieldRow>
                  </FormSection>
                )}
              </>
            )}

            {activeTab === "toegang" && (
              <div data-tour={requestsOwnBranches ? "settings-branches" : undefined} className="flex flex-col gap-7">
                {requestsOwnBranches && (
                  <FieldRow label="Vestigingen" hint="Je ziet alleen vacatures van vestigingen waar je toegang toe hebt. Vraag toegang aan voor de rest.">
                    {branches.length === 0 ? (
                      <div className="text-sm text-waves-muted sm:pt-2.5">Geen vestigingen gevonden voor je partner.</div>
                    ) : branches.map(b => {
                      const hasAccess = orig.branchIds.includes(b.id);
                      const isPending = myRequests.some(r => r.branch_id === b.id);
                      return (
                        <div key={b.id} className="flex min-h-12 items-center gap-4 rounded-[16px] border border-waves-line bg-white px-4 py-2.5">
                          <span className="flex-1 text-[15px] font-bold">{b.name}</span>
                          {hasAccess ? (
                            <span className="text-sm font-bold text-status-ok-fg">✓ Toegang</span>
                          ) : isPending ? (
                            <span className="text-sm font-medium text-waves-muted">Toegang aangevraagd</span>
                          ) : (
                            <Button variant="outline" size="sm" data-tour="branch-request" onClick={() => handleRequestBranch(b.id)} disabled={requesting === b.id}>
                              {requesting === b.id ? "Bezig…" : "Vraag toegang"}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </FieldRow>
                )}

                {showBranches && !requestsOwnBranches && (
                  <FieldRow label="Vestigingen" hint="Ziet alleen vacatures van deze vestigingen">
                    {requests.map(r => {
                      const decision = cur.requests[r.id];
                      return (
                        <div
                          key={r.id}
                          className={cn(
                            "flex flex-wrap items-center gap-3 rounded-[16px] border-2 px-4 py-3.5",
                            decision ? "border-waves-zee bg-waves-subtle" : "border-status-alert-bg bg-white",
                          )}
                        >
                          <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                            <div className="text-[15px] font-bold">
                              Vraagt toegang tot <span className="text-status-alert-fg">{r.branch_name}</span>
                            </div>
                            <div className="text-[13px] text-waves-body">{requestTime(r.created_at)}</div>
                          </div>
                          {decision ? (
                            <div className="flex shrink-0 items-center gap-2.5">
                              <span className="text-[13px] font-bold">
                                {decision === "grant" ? "Wordt toegekend bij opslaan" : "Wordt afgewezen bij opslaan"}
                              </span>
                              <button type="button" onClick={() => decide(r, null)} className="text-[13px] font-bold text-waves-accent hover:underline">
                                Herstel
                              </button>
                            </div>
                          ) : (
                            <div className="flex shrink-0 gap-2">
                              <Button variant="destructive" size="sm" onClick={() => decide(r, "deny")}>Afwijzen</Button>
                              <Button variant="ink" size="sm" onClick={() => decide(r, "grant")}>Toegang geven</Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!acc.partner_id ? (
                      <div className="text-sm text-waves-muted sm:pt-2.5">Kies eerst een partner bij Account.</div>
                    ) : branches.length === 0 ? (
                      <div className="text-sm text-waves-muted sm:pt-2.5">Geen vestigingen gevonden voor deze partner.</div>
                    ) : (
                      <CheckTileGrid>
                        {branches.map(b => (
                          <CheckTile
                            key={b.id}
                            label={b.name}
                            checked={cur.branchIds.includes(b.id)}
                            changed={!isNew && cur.branchIds.includes(b.id) !== orig.branchIds.includes(b.id)}
                            onClick={() => setCur(c => ({ ...c, branchIds: toggleIn(c.branchIds, b.id) }))}
                          />
                        ))}
                      </CheckTileGrid>
                    )}
                    {acc.partner_id && branches.length > 0 && cur.branchIds.length === 0 && (
                      <div className="text-[13px] text-status-warn-fg">Zonder vestiging ziet deze hiring manager geen vacatures.</div>
                    )}
                  </FieldRow>
                )}

                {showPartners && (
                  <FieldRow label="Partners" hint="Partners die deze account manager beheert">
                    {partners.length === 0 ? (
                      <div className="text-sm text-waves-muted sm:pt-2.5">Geen partners gevonden.</div>
                    ) : (
                      <CheckTileGrid>
                        {partners.map(p => (
                          <CheckTile
                            key={p.id}
                            label={p.name}
                            checked={cur.partnerIds.includes(p.id)}
                            disabled={!canEditPartners}
                            changed={!isNew && cur.partnerIds.includes(p.id) !== orig.partnerIds.includes(p.id)}
                            onClick={() => setCur(c => ({ ...c, partnerIds: toggleIn(c.partnerIds, p.id) }))}
                          />
                        ))}
                      </CheckTileGrid>
                    )}
                  </FieldRow>
                )}

                {acc.role === "partner_admin" && (
                  <div className="text-sm text-waves-muted">
                    Deze rol ziet alle vestigingen{acc.partner_id ? ` van ${partnerName(acc.partner_id)}` : ""}; vestigingen toewijzen is niet nodig.
                  </div>
                )}
                {acc.role === "admin" && (
                  <div className="text-sm text-waves-muted">
                    Deze rol heeft toegang tot alles{showPartners ? "; de partners hierboven bepalen alleen voor wie hij of zij als key account manager optreedt" : "; vestigingen of partners toewijzen is niet nodig"}.
                  </div>
                )}
              </div>
            )}

            {activeTab === "meldingen" && (
              cur.alerts && orig.alerts ? (
                <UserAlertsTab
                  userName={user?.name ?? ""}
                  ruleTypes={ruleTypes}
                  cur={cur.alerts}
                  orig={orig.alerts}
                  onChange={(rt: RuleType, patch: Partial<AlertDraft>) =>
                    setCur(c => ({ ...c, alerts: { ...c.alerts, [rt]: { ...c.alerts![rt]!, ...patch } } }))}
                  vestigingOptions={vestigingOptions}
                  partners={isWavesUser ? partners : undefined}
                />
              ) : (
                <div className="text-sm text-waves-muted">Laden…</div>
              )
            )}

            {activeTab === "afdelingen" && (
              cur.departments && orig.departments ? (
                <UserDepartmentsTab
                  cur={cur.departments}
                  orig={orig.departments}
                  branches={departmentBranches}
                  onChange={departments => setCur(c => ({ ...c, departments }))}
                />
              ) : (
                <div className="text-sm text-waves-muted">Laden…</div>
              )
            )}

            {activeTab === "review" && (
              cur.review && orig.review ? (
                <UserReviewTab
                  userName={user?.name ?? ""}
                  isSelf={isSelf}
                  cur={cur.review}
                  orig={orig.review}
                  showErrors={showErrors}
                  onChange={review => setCur(c => ({ ...c, review }))}
                />
              ) : (
                <div className="text-sm text-waves-muted">Laden…</div>
              )
            )}
          </div>

          {/* Footer */}
          <div data-tour="settings-footer" className="flex items-center gap-2.5 border-t border-waves-line px-5 py-3.5 sm:px-8">
            <div className={cn("flex min-w-0 flex-1 items-center gap-2 text-[13px] font-bold", footerStatus.cls)}>
              {"dot" in footerStatus && <span className="h-2 w-2 shrink-0 rounded-full bg-waves-zee" />}
              <span className="truncate">{footerStatus.text}</span>
            </div>
            <Button variant="outline" disabled={saving} onClick={() => requestClose()}>Annuleren</Button>
            <Button disabled={saving || (!isNew && changeCount === 0)} onClick={save}>
              {saving ? "Opslaan…" : isNew ? "Account aanmaken" : "Opslaan"}
            </Button>
          </div>
        </div>

        {confirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 p-8">
            <div role="alertdialog" className="flex w-full max-w-[420px] flex-col gap-4 rounded-card border border-waves-line bg-white p-6 shadow-pop">
              {confirm === "close" ? (
                <>
                  <div className="text-xl font-black tracking-[-0.01em]">Wijzigingen opslaan?</div>
                  <div className="text-sm leading-normal text-waves-body text-pretty">
                    {isNew
                      ? "Het account is nog niet aangemaakt. Wat je hebt ingevuld gaat verloren als je nu sluit."
                      : `Je hebt ${plural(changeCount, "wijziging", "wijzigingen")} voor ${user.name} die nog niet zijn opgeslagen.`}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2.5">
                    <Button variant="outline" className="text-status-alert-fg" onClick={close}>Niet opslaan</Button>
                    <Button variant="outline" onClick={cancelConfirm}>Terug</Button>
                    <Button onClick={save}>{isNew ? "Aanmaken" : "Opslaan"}</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xl font-black tracking-[-0.01em]">Account verwijderen?</div>
                  <div className="text-sm leading-normal text-waves-body text-pretty">
                    <span className="font-bold text-waves-ink">{user?.name}</span> ({user?.email}) kan daarna niet meer inloggen. Dit kan niet ongedaan worden gemaakt.
                  </div>
                  <div className="flex flex-wrap justify-end gap-2.5">
                    <Button variant="outline" disabled={busy} onClick={() => setConfirm(null)}>Annuleren</Button>
                    <Button className="bg-status-alert-fg hover:bg-status-alert-fg/90" disabled={busy} onClick={handleDelete}>
                      {busy ? "Verwijderen…" : "Verwijderen"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
